/**
 * CommentsSidebar - Notion/Google Docs style comment panel
 *
 * Features:
 * - Shows all comments for current document
 * - Grouped by status (open vs resolved)
 * - Click to scroll to commented text
 * - Reply and resolve inline
 */

import { useState } from 'react';
import { MessageCircle, Check, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import type { InlineComment, CommentReply } from './InlineComment';
import { CommentThread } from './CommentThread';

interface CommentsSidebarProps {
  comments: InlineComment[];
  repliesMap: Map<string, CommentReply[]>;
  activeCommentId: string | null;
  onCommentClick: (comment: InlineComment) => void;
  onReply: (commentId: string, content: string) => void;
  onResolve: (commentId: string) => void;
  onAskMartha?: (context: { selectedText: string; commentContent: string }) => void;
  isSubmitting?: boolean;
}

export function CommentsSidebar({
  comments,
  repliesMap,
  activeCommentId,
  onCommentClick,
  onReply,
  onResolve,
  onAskMartha,
  isSubmitting,
}: CommentsSidebarProps) {
  const [showResolved, setShowResolved] = useState(false);

  // Split comments by status
  const openComments = comments.filter((c) => c.status === 'open');
  const resolvedComments = comments.filter((c) => c.status === 'resolved');

  if (comments.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
        <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mb-3">
          <MessageCircle size={20} className="text-neutral-400" />
        </div>
        <p className="text-sm text-neutral-500 mb-1">No comments yet</p>
        <p className="text-xs text-neutral-400">
          Select text in the document to add a comment
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Open Comments */}
      {openComments.length > 0 && (
        <div className="p-3 space-y-3">
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 px-1">
            Open ({openComments.length})
          </h4>
          {openComments.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              replies={repliesMap.get(comment.id) || []}
              isActive={activeCommentId === comment.id}
              onClick={() => onCommentClick(comment)}
              onReply={(content) => onReply(comment.id, content)}
              onResolve={() => onResolve(comment.id)}
              onAskMartha={onAskMartha}
              isSubmitting={isSubmitting}
            />
          ))}
        </div>
      )}

      {/* Resolved Comments (collapsible) */}
      {resolvedComments.length > 0 && (
        <div className="border-t border-neutral-100">
          <button
            onClick={() => setShowResolved(!showResolved)}
            className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-neutral-500 hover:bg-neutral-50"
          >
            <span className="flex items-center gap-1.5">
              <Check size={12} className="text-emerald-500" />
              Resolved ({resolvedComments.length})
            </span>
            {showResolved ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>

          {showResolved && (
            <div className="p-3 pt-0 space-y-3">
              {resolvedComments.map((comment) => (
                <CommentCard
                  key={comment.id}
                  comment={comment}
                  replies={repliesMap.get(comment.id) || []}
                  isActive={activeCommentId === comment.id}
                  onClick={() => onCommentClick(comment)}
                  onReply={(content) => onReply(comment.id, content)}
                  onResolve={() => onResolve(comment.id)}
                  isSubmitting={isSubmitting}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface CommentCardProps {
  comment: InlineComment;
  replies: CommentReply[];
  isActive?: boolean;
  onClick: () => void;
  onReply: (content: string) => void;
  onResolve: () => void;
  onAskMartha?: (context: { selectedText: string; commentContent: string }) => void;
  isSubmitting?: boolean;
}

function CommentCard({
  comment,
  replies,
  isActive,
  onClick,
  onReply,
  onResolve,
  onAskMartha,
  isSubmitting,
}: CommentCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isResolved = comment.status === 'resolved';

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      className={`
        rounded-lg border transition-all cursor-pointer
        ${isActive
          ? 'border-coral-300 bg-coral-50/50 shadow-sm'
          : isResolved
            ? 'border-neutral-100 bg-neutral-50/50 hover:border-neutral-200'
            : 'border-neutral-200 bg-white hover:border-coral-200 hover:shadow-sm'
        }
      `}
    >
      {/* Collapsed preview */}
      <div
        onClick={() => {
          onClick();
          setIsExpanded(!isExpanded);
        }}
        className="p-3"
      >
        {/* Quoted text snippet */}
        <p className="text-xs text-neutral-500 italic mb-2 line-clamp-1 border-l-2 border-coral-200 pl-2">
          "{comment.selectedText}"
        </p>

        {/* Comment preview */}
        <div className="flex items-start gap-2">
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium
              ${comment.authorType === 'martha' ? 'bg-coral-100 text-coral-600' : 'bg-neutral-100 text-neutral-600'}`}
          >
            {comment.authorName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-xs font-medium text-neutral-700 truncate">
                {comment.authorName}
              </span>
              <span className="text-[10px] text-neutral-400">{formatDate(comment.createdAt)}</span>
              {replies.length > 0 && (
                <span className="text-[10px] text-neutral-400 flex items-center gap-0.5">
                  · {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                </span>
              )}
            </div>
            <p className="text-xs text-neutral-600 line-clamp-2">{comment.content}</p>
          </div>
        </div>
      </div>

      {/* Expanded thread */}
      {isExpanded && !isResolved && (
        <div className="border-t border-neutral-100 p-3">
          <CommentThread
            comment={comment}
            replies={replies}
            onReply={onReply}
            onResolve={onResolve}
            onAskMartha={onAskMartha}
            isSubmitting={isSubmitting}
          />
        </div>
      )}
    </div>
  );
}

export default CommentsSidebar;
